/**
 * Markdown Document Viewer Component
 *
 * Renders markdown documents with support for:
 * - Headings (H1, H2, H3)
 * - Paragraphs
 * - Bullet lists with nested items
 * - Ordered lists with nested items
 * - Horizontal dividers
 * - Inline formatting (bold, italic, links)
 *
 * Nested lists are supported through recursive rendering of subItems.
 */
import { Flex, FontSize, Link, Text, useTheme } from "@repo/ui";
import { useStyles } from "purse-styles";
import type {
	BulletListNode,
	HeadingNode,
	HorizontalDividerNode,
	ImageNode,
	InlineNode,
	ListItemNode,
	MarkdownDoc,
	MarkdownNode,
	OrderedListNode,
	ParagraphNode,
} from "../markdown/parser";

const LIST_ITEM_PADDING_SIZE_SM = 12;
const LIST_ITEM_PADDING_SIZE_MD = 16;
const LIST_ITEM_PADDING_SIZE_LG = 24;

const getListItemPaddingSize = (size: FontSize) => {
	if (size === "sm" || size === "xs" || size === "xxs") {
		return LIST_ITEM_PADDING_SIZE_SM;
	}
	if (size === "lg" || size === "xl" || size === "xxl" || size === "xxxl") {
		return LIST_ITEM_PADDING_SIZE_LG;
	}
	return LIST_ITEM_PADDING_SIZE_MD;
};

type MarkdownDocViewProps = {
	doc: MarkdownDoc;
	size?: FontSize;
};

export function MarkdownDocView({ doc, size = "prose" }: MarkdownDocViewProps) {
	return (
		<Flex column>
			{doc.content.map((node, index) => (
				<MarkdownNodeView key={index} node={node} size={size} />
			))}
		</Flex>
	);
}

type MarkdownNodeViewProps = {
	node: MarkdownNode;
	size: FontSize;
};

function MarkdownNodeView({ node, size }: MarkdownNodeViewProps) {
	switch (node.type) {
		case "heading":
			return <HeadingView node={node} size={size} />;
		case "paragraph":
			return <ParagraphView node={node} size={size} />;
		case "bulletList":
			return <BulletListView node={node} size={size} />;
		case "orderedList":
			return <OrderedListView node={node} size={size} />;
		case "horizontalDivider":
			return <HorizontalDividerView node={node} />;
		default:
			return null;
	}
}

function HeadingView({
	node,
	size: docSize,
}: {
	node: HeadingNode;
	size: FontSize;
}) {
	const theme = useTheme();

	// More pronounced size differences for clear hierarchy
	const getHeadingSize = (level: number, docSize: FontSize): FontSize => {
		// For prose (15px base): H1 = xxl (24px), H2 = xl (20px), H3 = lg (18px)
		if (docSize === "prose" || docSize === "md") {
			return level === 1 ? "xxl" : level === 2 ? "xl" : "lg";
		}
		// For prose-sm (13px base): H1 = xl (20px), H2 = lg (18px), H3 = md (15px)
		if (docSize === "prose-sm" || docSize === "sm") {
			return level === 1 ? "xl" : level === 2 ? "lg" : "md";
		}
		// For larger base sizes
		if (docSize === "lg") {
			return level === 1 ? "xxxl" : level === 2 ? "xxl" : "xl";
		}
		// Default fallback
		return level === 1 ? "xl" : level === 2 ? "lg" : "md";
	};

	const getHeadingPadding = (level: number) => {
		// Generous vertical padding for visual hierarchy
		if (level === 1) {
			return { paddingTop: theme.spacing.xl, paddingBottom: theme.spacing.sm };
		}
		if (level === 2) {
			return { paddingTop: theme.spacing.lg, paddingBottom: theme.spacing.xs };
		}
		return { paddingTop: theme.spacing.md, paddingBottom: theme.spacing.xxs };
	};

	const size = getHeadingSize(node.level, docSize);
	const padding = getHeadingPadding(node.level);

	return (
		<Flex style={padding}>
			<Text size={size} weight="semibold">
				<InlineContentView content={node.content} size={size} />
			</Text>
		</Flex>
	);
}

function ParagraphView({
	node,
	size,
}: {
	node: ParagraphNode;
	size: FontSize;
}) {
	return (
		<Flex paddingVertical="xs">
			<Text as="p" size={size}>
				<InlineContentView content={node.content} size={size} />
			</Text>
		</Flex>
	);
}

export function BulletListView({
	node,
	size,
}: {
	node: BulletListNode;
	size: FontSize;
}) {
	const theme = useTheme();
	const paddingSize = `${getListItemPaddingSize(size)}px`;
	const listStyles = useStyles({
		paddingLeft: paddingSize,
		margin: 0,
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing.xxs,
		listStyleType: "none",
	});

	return (
		<ul className={listStyles}>
			{node.items.map((item, index) => (
				<BulletListItemView key={index} item={item} level={0} size={size} />
			))}
		</ul>
	);
}

function OrderedListView({
	node,
	size,
}: {
	node: OrderedListNode;
	size: FontSize;
}) {
	const theme = useTheme();
	const paddingSize = `${getListItemPaddingSize(size)}px`;
	const listStyles = useStyles({
		paddingLeft: paddingSize,
		margin: 0,
		listStyleType: "none",
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing.xxs,
		counterReset: "list-counter",
	});

	return (
		<ol className={listStyles}>
			{node.items.map((item, index) => (
				<OrderedListItemView key={index} item={item} level={0} size={size} />
			))}
		</ol>
	);
}

type BulletListItemViewProps = {
	item: ListItemNode;
	level: number;
	size: FontSize;
};

function BulletListItemView({ item, level, size }: BulletListItemViewProps) {
	const theme = useTheme();

	const paddingSize = `${getListItemPaddingSize(size)}px`;
	const itemStyles = useStyles({
		position: "relative",
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing.xxs,
		paddingLeft: level > 0 ? paddingSize : "0",
	});

	const fontSize = theme.fontStyles[size];
	const minWidth = paddingSize;

	const bulletStyles = useStyles({
		position: "relative",
		"&::before": {
			content: "'•'",
			position: "absolute",
			color: theme.colors.sand11,
			fontSize: fontSize.fontSize,
			lineHeight: fontSize.lineHeight,
			fontFamily: theme.fontFamilies.Inter,
			minWidth: minWidth,
			right: "100%",
			top: -1,
		},
	});

	return (
		<li className={itemStyles}>
			<div className={bulletStyles}>
				<Text size={size} as="p">
					<InlineContentView content={item.content} size={size} />
				</Text>
			</div>
			{item.subItems && item.subItems.length > 0 && (
				<BulletListView
					node={{
						type: "bulletList",
						items: item.subItems,
					}}
					size={size}
				/>
			)}
		</li>
	);
}

type OrderedListItemViewProps = {
	item: ListItemNode;
	level: number;
	size: FontSize;
};

function OrderedListItemView({ item, level, size }: OrderedListItemViewProps) {
	const theme = useTheme();

	const paddingSize = `${getListItemPaddingSize(size)}px`;
	const itemStyles = useStyles({
		position: "relative",
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing.xxs,
		paddingLeft: level > 0 ? paddingSize : "0",
	});

	const fontSize = theme.fontStyles[size];
	const minWidth = paddingSize;

	const numberStyles = useStyles({
		position: "relative",
		counterIncrement: "list-counter",
		"&::before": {
			content: "counter(list-counter) '.'",
			position: "absolute",
			fontVariantNumeric: "tabular-nums",
			color: theme.colors.sand11,
			fontSize: fontSize.fontSize,
			lineHeight: fontSize.lineHeight,
			fontFamily: theme.fontFamilies.Inter,
			minWidth: minWidth,
			right: "100%",
		},
	});

	return (
		<li className={itemStyles}>
			<div className={numberStyles}>
				<Text size={size} as="p">
					<InlineContentView content={item.content} size={size} />
				</Text>
			</div>
			{item.subItems && item.subItems.length > 0 && (
				<OrderedListView
					node={{
						type: "orderedList",
						items: item.subItems,
					}}
					size={size}
				/>
			)}
		</li>
	);
}

function HorizontalDividerView({
	node: _node,
}: {
	node: HorizontalDividerNode;
}) {
	const theme = useTheme();
	return (
		<div
			style={{
				borderTop: `1px solid ${theme.colors.sandA3}`,
				margin: "8px 0",
			}}
		/>
	);
}

type InlineContentViewProps = {
	content: InlineNode[];
	size: FontSize;
};

function InlineContentView({ content, size }: InlineContentViewProps) {
	return (
		<>
			{content.map((node, index) => (
				<InlineNodeView key={index} node={node} size={size} />
			))}
		</>
	);
}

type InlineNodeViewProps = {
	node: InlineNode;
	size: FontSize;
};

function InlineNodeView({ node, size }: InlineNodeViewProps) {
	switch (node.type) {
		case "text":
			return <Text size={size}>{node.text}</Text>;
		case "bold":
			return (
				<Text weight="semibold" as="span" size={size}>
					<InlineContentView content={node.content} size={size} />
				</Text>
			);
		case "italic":
			return (
				<Text italic as="span" size={size}>
					<InlineContentView content={node.content} size={size} />
				</Text>
			);
		case "link":
			return (
				<Link href={node.href} target="_blank" rel="noopener noreferrer">
					<InlineContentView content={node.content} size={size} />
				</Link>
			);
		case "image":
			return <ImageView node={node} />;
		default:
			return null;
	}
}

function ImageView({ node }: { node: ImageNode }) {
	const theme = useTheme();

	const imgStyles = useStyles({
		maxWidth: "100%",
		height: "auto",
		display: "block",
	});

	return (
		<Flex
			shadow="subtle"
			borderRadius="sm"
			overflow="hidden"
			style={{ marginTop: theme.spacing.sm, marginBottom: theme.spacing.sm }}
		>
			<img src={node.src} alt={node.alt} className={imgStyles} />
		</Flex>
	);
}
